import React, { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Button } from '../ui/Button';
import { AlertTriangle, Loader2 } from 'lucide-react';

interface SignUpComponentProps {
  onSuccess?: () => void;
}

export function SignUpComponent({ onSuccess }: SignUpComponentProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    // Basic validation
    if (!firstName.trim() || !lastName.trim()) {
      setError('Fornavn og etternavn er påkrevd');
      return;
    }
    
    if (!email.trim()) {
      setError('E-post er påkrevd');
      return;
    }
    
    if (!password.trim()) {
      setError('Passord er påkrevd');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passordene må være like');
      return;
    }
    
    if (password.length < 6) {
      setError('Passordet må være minst 6 tegn');
      return;
    }
    
    try {
      setLoading(true);
      
      // Check if Supabase is connected
      if (!import.meta.env.VITE_SUPABASE_URL || !import.meta.env.VITE_SUPABASE_ANON_KEY) {
        setError('Supabase er ikke konfigurert. Klikk "Connect to Supabase" for å sette opp tilkoblingen.');
        return;
      }
      
      // Create user with Supabase Auth
      const { data, error } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password: password,
        options: {
          data: {
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            full_name: `${firstName.trim()} ${lastName.trim()}`
          }
        }
      });
      
      if (error) {
        console.error("Signup error:", error);
        if (error.message.includes("already registered")) {
          setError('Denne e-postadressen er allerede registrert');
        } else {
          setError(error.message);
        }
        return;
      }
      
      // If signup was successful, call onSuccess callback
      if (data.user) {
        console.log('User created:', data.user);
        
        // Profil, user_belong og board_members opprettes automatisk via database trigger (handle_new_user)
        // Triggeren setter:
        // - Profil med first_name og last_name
        // - user_belong med residence_id=4, apartment_id=1 og role='board'
        // - board_members med position='member' og start_date=CURRENT_DATE
        
        // Vent litt for å sikre at triggeren har kjørt
        await new Promise(resolve => setTimeout(resolve, 500));
        
        if (onSuccess) {
          setTimeout(() => {
            onSuccess();
          }, 100);
        }
      }
    } catch (err) {
      console.error('Signup error:', err);
      setError(err instanceof Error ? err.message : 'En ukjent feil oppstod');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* First Name field */}
        <div>
          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
            Fornavn
          </label>
          <input
            id="firstName"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-1 focus:outline-none focus:ring-condo-dark"
            required
            autoComplete="given-name"
            name="firstName"
          />
        </div>

        {/* Last Name field */}
        <div>
          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
            Etternavn
          </label>
          <input
            id="lastName"
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-1 focus:outline-none focus:ring-condo-dark"
            required
            autoComplete="family-name"
            name="lastName"
          />
        </div>
        
        {/* Email field */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            E-post
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-1 focus:outline-none focus:ring-condo-dark"
            placeholder="din@epost.no"
            required
            autoComplete="email"
            name="email"
          />
        </div>
        
        {/* Password field */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
            Passord
          </label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-1 focus:outline-none focus:ring-condo-dark"
            placeholder="••••••••"
            required
            autoComplete="new-password"
            name="password"
          />
        </div>
        
        {/* Confirm Password field */}
        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
            Bekreft passord
          </label>
          <input
            id="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-1 focus:outline-none focus:ring-condo-dark"
            placeholder="••••••••"
            required
            autoComplete="new-password"
            name="confirmPassword"
          />
        </div>
        
        {/* Error message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3 flex items-start">
            <AlertTriangle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        )}
        
        {/* Submit button */}
        <Button
          type="submit"
          disabled={loading}
          className="w-full flex items-center justify-center"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Oppretter bruker...
            </>
          ) : (
            'Opprett bruker'
          )}
        </Button>
      </form>
    </div>
  );
}